import { useNavigate } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import API from '../../api';
import { APP_ROUTE, LOCAL_STORAGE_TYPES } from '../../constants';
import {
  ReportsContainer,
  UserContainer,
  ClientContainer,
} from '../../providers';
import { IsLoadingReportState } from '../../states';
import useLocalStorage from '../localstorage';

const useGetReports = () => {
  const {
    appendReports,
    updateTotal,
    updateFirstLoad,
    updateIsLoaded,
    updateIsLoading,
  } = ReportsContainer.useContainer();
  const { state: userState } = UserContainer.useContainer();
  const { state: clientState } = ClientContainer.useContainer();

  const setIsLoading = useSetRecoilState(IsLoadingReportState);

  const getReports = async query => {
    const clientId = clientState.selectedClient?.id;
    if (clientId) {
      updateIsLoading(true);
      const response = await API.reports.find({
        clientId,
        businessId: userState?.business?.id,
        ...query,
      });
      const { data: reports, total } = response;
      const reportsWithPageType =
        reports?.map(report => ({
          ...report,
          pageTypes: report.accounts.map(a => a.provider.type),
        })) ?? [];
      appendReports(reportsWithPageType, query.$skip ? query.$skip : 0);
      updateTotal(total);
      updateFirstLoad(false);
      setIsLoading(false);
      updateIsLoading(false);
      updateIsLoaded(true);
    }
  };

  return getReports;
};

const useGetReportById = () => {
  const { state: userState } = UserContainer.useContainer();
  const { state: clientState } = ClientContainer.useContainer();
  const { get } = useLocalStorage();
  const navigate = useNavigate();

  const getReportById = async id => {
    const { client } = get(LOCAL_STORAGE_TYPES.WORKSPACE);

    const query = {
      clientId: clientState?.selectedClient?.id ?? client,
      businessId: userState?.business?.id,
      fields: 'account,schedule',
    };
    if (query.clientId && query.businessId) {
      try {
        const response = await API.reports.get(id, query);
        const reportsWithPageType =
          {
            ...response,
            pageTypes: response.accounts.map(a => a.provider.type),
          } ?? [];
        return reportsWithPageType;
      } catch (err) {
        const workspaceId = clientState?.selectedClient?.id ?? client;
        navigate(
          `/ws/${workspaceId}/${APP_ROUTE.REPORTING}/${APP_ROUTE.REPORTS}`
        );
      }
    }
    return [];
  };
  return getReportById;
};

const useCreateReport = () => {
  const {
    state: reportsState,
    setPageNum,
    updateTotal,
    updateReports,
    updateFirstLoad,
  } = ReportsContainer.useContainer();
  const { state: userState } = UserContainer.useContainer();
  const { state: clientState } = ClientContainer.useContainer();

  const createReport = async data => {
    const businessId = userState.business?.id;
    const clientId = clientState?.selectedClient?.id;
    const response = await API.reports.create({
      businessId,
      ...data,
      clientId,
    });
    const reportWithPageType =
      {
        ...response,
        pageTypes: response.accounts.map(a => a.provider.type),
      } ?? [];
    updateReports([reportWithPageType, ...reportsState.reports]);
    updateFirstLoad(false);
    // Increment total by 1.
    updateTotal(reportsState.total + 1);
    // Send user to page number 1 to see latest report.
    setPageNum(1);
  };
  return createReport;
};

const useDeleteReportById = () => {
  const {
    state: reportsState,
    updateReports,
    updateTotal,
  } = ReportsContainer.useContainer();
  const { state: userState } = UserContainer.useContainer();

  const deleteReportById = async (id, deleteFromDrive = false) => {
    const businessId = userState?.business?.id;
    await API.reports.delete(id, {
      businessId,
      deleteFromDrive,
    });

    updateReports(reportsState.reports.filter(report => report.id !== id));
    // Decrement total by 1.
    updateTotal(reportsState.total - 1);
  };
  return deleteReportById;
};

const useUpdateReportById = () => {
  const { state: userState } = UserContainer.useContainer();
  const {
    state: reportsState,
    updateReports,
  } = ReportsContainer.useContainer();

  // eslint-disable-next-line consistent-return
  const updateReportById = async (id, payload) => {
    const businessId = userState?.business?.id;
    if (businessId) {
      const response = await API.reports.update(
        `${id}/?businessId=${businessId}&fields=account,schedule`,
        payload
      );

      const reportsWithPageType =
        {
          ...response,
          pageTypes: response.accounts.map(a => a.provider.type),
        } ?? [];

      const updatedReports = reportsState.reports.map(report => {
        if (report.id === id) {
          return reportsWithPageType;
        }
        return report;
      });
      updateReports([...updatedReports]);
      return reportsWithPageType;
    }
  };

  return updateReportById;
};

const useResyncReport = () => {
  const { state: userState } = UserContainer.useContainer();

  const resyncReport = async (id, scheduleId) => {
    const query = {
      businessId: userState?.business?.id,
      fields: 'account,schedule',
    };
    const response = await API.reportsResync.create({ id, scheduleId }, query);
    let reportsWithPageType = [];
    if (Array.isArray(response)) {
      reportsWithPageType =
        {
          ...response[0],
          pageTypes: response[0].accounts.map(a => a.provider.type),
        } ?? [];

      return reportsWithPageType;
    }
    reportsWithPageType =
      {
        ...response,
        pageTypes: response.accounts.map(a => a.provider.type),
      } ?? [];
    return reportsWithPageType;
  };
  return resyncReport;
};

export {
  useCreateReport,
  useGetReports,
  useGetReportById,
  useDeleteReportById,
  useUpdateReportById,
  useResyncReport,
};
